import { Pagination } from "@mui/material";
import { ChevronLeft, MoreHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { checkTagValidity } from "../../Utils/Utils";
import { ADD_BRIN, DELETE_BRIN } from "../../Utils/constant";
import { Port } from "../../components/Icons/Port";
import { UpdatePort } from "../../components/Pbo/UpdatePort";
import { Loader } from "../../components/Widget/Loader";
import { deleteOne, store } from "../../services/service";
import { usePBOStore } from "../../store/pboStore/listPboStore";
import { usePortStore } from "../../store/pboStore/listPort";

const statusConfig = {
  LIBRE: { label: "Libre", cls: "bg-yellow-100 text-yellow-700" },
  RESERVER: { label: "Réservé", cls: "bg-blue-100 text-blue-700" },
  CONNECTER: { label: "Connecté", cls: "bg-green-100 text-green-700" },
  DEFECTUEUX: { label: "Défectueux", cls: "bg-red-100 text-red-600" },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || {
    label: status?.toLowerCase(),
    cls: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
};

export const PboPort = () => {
  const { id, nom } = useParams();
  const listPorts = usePortStore();
  const coupleurByPBO = usePBOStore();
  const [selectItem, setSelectItem] = useState(null);
  const [statutFilter, setStatutFilter] = useState("LIBRE");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [isSubmit, setIsSubmit] = useState(false);
  const [error, setError] = useState("");

  const submit = (data) => {
    setIsSubmit(true);
    store(ADD_BRIN, "application/json", data)
      .then((res) => {
        if (res.status === 200) {
          listPorts.getData(`/${id}?page=0&size=30&status=`);
          document.getElementById("modal_add").close();
          toast.success("Brin enregistré");
          setIsSubmit(false);
          reset();
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        setError(err?.response?.data?.donnee);
      });
  };

  useEffect(() => {
    coupleurByPBO.getCoupleurByPBO(id);
    listPorts.getData(`/${id}?page=0&size=30&status=${statutFilter}`);
  }, [statutFilter]);

  const more = (page) =>
    listPorts.getData(`/${id}?page=${page - 1}&size=30&status=${statutFilter}`);

  const inputCls =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition";
  const selectCls =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition";
  const closeBtnCls =
    "w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm font-semibold transition";

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mt-4 mb-1">
        <button
          onClick={() => window.history.back()}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition"
        >
          <ChevronLeft size={16} />
        </button>
        <div>
          <p className="text-2xl font-bold text-gray-900 tracking-tight">
            Brins — {decodeURIComponent(nom)}
          </p>
          <p className="text-sm text-gray-400 mt-0.5">
            Gestion des brins et de leurs états
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 py-4 border-b border-gray-100 mb-6">
        <select
          value={statutFilter}
          onChange={(e) => setStatutFilter(e.target.value)}
          className="h-10 px-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition"
        >
          <option value="LIBRE">Libre</option>
          <option value="RESERVER">Réservé</option>
          <option value="CONNECTER">Connecté</option>
          <option value="DEFECTUEUX">Défectueux</option>
        </select>

        {checkTagValidity("AJOUTER_PORT") && (
          <button
            onClick={() => {
              reset();
              setError("");
              document.getElementById("modal_add").showModal();
            }}
            className="ml-auto h-10 px-4 bg-yellow-400 hover:bg-yellow-300 text-gray-900 text-sm font-semibold rounded-xl transition"
          >
            Ajouter un brin
          </button>
        )}
      </div>

      {/* Add Modal */}
      <dialog id="modal_add" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">Nouveau brin</h2>
            <form method="dialog">
              <button className={closeBtnCls}>✕</button>
            </form>
          </div>
          {error && <p className="text-xs text-red-500 mb-4">{error}</p>}
          <form onSubmit={handleSubmit(submit)} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Désignation du brin <span className="text-red-500">*</span>
              </label>
              <input
                {...register("designation", { required: true })}
                type="text"
                placeholder="ex: Brin-A1"
                className={inputCls}
              />
              {errors.designation && (
                <p className="text-xs text-red-500 mt-1">
                  Ce champ est obligatoire
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Coupleur <span className="text-red-500">*</span>
              </label>
              <select
                {...register("coupleurId", { required: true })}
                className={selectCls}
              >
                <option value="" disabled>
                  Sélectionner un coupleur
                </option>
                {coupleurByPBO?.coupleurs?.map((item) => (
                  <option key={item.id} value={item.id}>
                    Coupleur-{item.numero}
                  </option>
                ))}
              </select>
              {errors.coupleurId && (
                <p className="text-xs text-red-500 mt-1">
                  Ce champ est obligatoire
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmit}
              className={`w-full h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition ${
                isSubmit
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-yellow-400 hover:bg-yellow-300 text-gray-900"
              }`}
            >
              {isSubmit ? (
                <>
                  <span className="loading loading-spinner loading-xs" />{" "}
                  Enregistrement…
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
          </form>
        </div>
      </dialog>

      {/* Delete Modal */}
      <dialog id="delete" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <p className="text-base font-bold text-gray-900 mb-1">
            Confirmer la suppression
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Le brin{" "}
            <span className="font-semibold text-gray-800">
              {selectItem?.designation}
            </span>{" "}
            sera définitivement supprimé.
          </p>
          <div className="flex justify-end gap-2">
            <form method="dialog">
              <button className="h-9 px-4 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition">
                Annuler
              </button>
            </form>
            <form method="dialog">
              <button
                onClick={() => {
                  deleteOne(DELETE_BRIN, selectItem.id)
                    .then(() => {
                      toast.success("Brin supprimé");
                      listPorts.getData(`/${id}?page=0&size=30&status=`);
                    })
                    .catch(() => {});
                }}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition"
              >
                Supprimer
              </button>
            </form>
          </div>
        </div>
      </dialog>

      {/* Details Modal */}
      <dialog id="intention" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-lg">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">Détails du brin</h2>
            <form method="dialog">
              <button className={closeBtnCls}>✕</button>
            </form>
          </div>
          <div className="space-y-3">
            {[
              {
                label: "Numéro ticket",
                value: selectItem?.abonnement?.eligibilite?.numeroTicket,
              },
              {
                label: "Client",
                value: selectItem?.abonnement?.client?.nomPrenoms,
              },
              {
                label: "Commune / Quartier",
                value: `${selectItem?.abonnement?.domicile?.quartier?.commune?.designation ?? ""}, ${selectItem?.abonnement?.domicile?.quartier?.designation ?? ""}`,
              },
              {
                label: "Type de maison",
                value: selectItem?.abonnement?.batiment?.designation,
              },
              {
                label: "Numéro FTTH",
                value: selectItem?.abonnement?.numeroFtth,
              },
              {
                label: "Étape",
                value: selectItem?.abonnement?.status
                  ?.toLowerCase()
                  .replace(/_/g, " "),
              },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="flex items-start justify-between py-2.5 border-b border-gray-100 last:border-0"
              >
                <span className="text-xs font-medium text-gray-500 w-36 shrink-0">
                  {label}
                </span>
                <span className="text-xs font-semibold text-blue-700 text-right">
                  {value || "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </dialog>

      {/* Grid */}
      {checkTagValidity("LISTE_PORT") && (
        <>
          {listPorts?.loading ? (
            <Loader />
          ) : listPorts?.data?.data?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                <svg
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <p className="text-sm text-gray-400">Aucun brin trouvé</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {listPorts?.data?.data?.map((item) => (
                <div
                  key={item.id}
                  className="relative bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 hover:shadow-md hover:border-yellow-300 transition-all duration-200"
                >
                  {/* Menu */}
                  <div className="dropdown dropdown-end absolute top-2 right-2">
                    <div
                      tabIndex={0}
                      role="button"
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 transition cursor-pointer"
                    >
                      <MoreHorizontal size={15} />
                    </div>
                    <ul
                      tabIndex={0}
                      className="dropdown-content z-10 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg w-44 p-1.5"
                    >
                      {item?.abonnement !== null && (
                        <li>
                          <button
                            className="w-full h-8 px-3 text-left text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition"
                            onClick={() => {
                              setSelectItem(item);
                              document.getElementById("intention").showModal();
                            }}
                          >
                            Voir les détails
                          </button>
                        </li>
                      )}
                      {checkTagValidity("MODIFIER_STATUT_PORT") && (
                        <li>
                          <button
                            className="w-full h-8 px-3 text-left text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition"
                            onClick={() => {
                              setSelectItem(item);
                              document
                                .getElementById(`edit${item.id}`)
                                .showModal();
                            }}
                          >
                            Modifier
                          </button>
                        </li>
                      )}
                      {checkTagValidity("SUPPRIMER_PORT") && (
                        <li>
                          <button
                            className="w-full h-8 px-3 text-left text-xs font-medium text-red-600 rounded-lg hover:bg-red-50 transition"
                            onClick={() => {
                              setSelectItem(item);
                              document.getElementById("delete").showModal();
                            }}
                          >
                            Supprimer
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Edit Modal per item */}
                  <dialog id={`edit${item.id}`} className="modal">
                    <div className="modal-box rounded-2xl p-6 max-w-md">
                      <div className="flex items-center justify-between mb-5">
                        <h2 className="text-lg font-bold text-gray-900">
                          Modifier le brin
                        </h2>
                        <form method="dialog">
                          <button className={closeBtnCls}>✕</button>
                        </form>
                      </div>
                      <UpdatePort
                        portId={item?.id}
                        defaultValue={item?.status}
                        designation={item?.designation}
                      />
                    </div>
                  </dialog>

                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <Port />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 pr-6">
                    <p className="text-xs font-semibold text-gray-900 truncate">
                      {item.designation}
                    </p>
                    <div className="mt-1.5">
                      <StatusBadge status={item?.status} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {listPorts?.data?.totalPages > 1 && (
        <div className="flex justify-end mt-6">
          <Pagination
            onChange={(_, v) => more(v)}
            count={listPorts?.data?.totalPages}
            variant="outlined"
            color="primary"
            shape="rounded"
            size="small"
          />
        </div>
      )}
    </div>
  );
};
